import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { KeyRound, MapPin, CheckCircle2, Loader2, Download } from "lucide-react";

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" }) : "";

export default function PublicSurrender() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = "Key Surrender Receipt";
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    axios.get(`${API}/surrender/${token}`)
      .then(({ data }) => { setData(data); setState("ok"); })
      .catch(() => setState("notfound"));
    return () => { document.head.removeChild(m); };
  }, [token]);

  if (state === "loading") {
    return <div className="min-h-screen grid place-items-center bg-[#FAF7F2]"><Loader2 className="animate-spin text-brand" /></div>;
  }
  if (state === "notfound") {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF7F2] px-6 text-center">
        <div data-testid="surrender-notfound">
          <p className="font-heading text-2xl font-bold text-navy">Receipt unavailable</p>
          <p className="mt-2 text-[#64748B]">This surrender receipt link is invalid or no longer available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="border-b border-[#E2E8F0] bg-white/80 backdrop-blur">
        <div className="max-w-2xl mx-auto px-6 py-4"><Logo /></div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10" data-testid="surrender-receipt">
        <div className="rounded-3xl border border-[#E2E8F0] bg-white overflow-hidden shadow-sm">
          <div className="bg-navy px-6 py-6 text-white">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-amber">
              <CheckCircle2 size={14} /> Move-out complete
            </span>
            <h1 className="mt-3 font-heading text-2xl font-bold flex items-center gap-2">
              <KeyRound size={22} className="text-amber" /> Keys surrendered
            </h1>
            <p className="mt-1 text-sm text-white/70">{data.message}</p>
          </div>

          <div className="p-6 space-y-5">
            <img
              src={`${API}${data.key_photo_url}`}
              alt="Keys at surrender"
              data-testid="surrender-key-photo"
              className="w-full max-h-[420px] object-contain rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC]"
            />

            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-[#94A3B8] font-medium">Property</dt>
                <dd className="text-navy font-semibold text-right" data-testid="surrender-address">{data.address}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-[#94A3B8] font-medium">Surrendered</dt>
                <dd className="text-navy font-semibold text-right" data-testid="surrender-timestamp">{fmtDateTime(data.surrendered_at)}</dd>
              </div>
              {data.location && (
                <div className="flex justify-between gap-4 items-center">
                  <dt className="text-[#94A3B8] font-medium">Location</dt>
                  <dd className="text-right">
                    <a
                      data-testid="surrender-location-link"
                      href={`https://www.openstreetmap.org/?mlat=${data.location.lat}&mlon=${data.location.lng}#map=18/${data.location.lat}/${data.location.lng}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-brand font-semibold hover:underline">
                      <MapPin size={14} /> {data.location.lat.toFixed(5)}, {data.location.lng.toFixed(5)}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
        <div className="mt-4 flex justify-center">
          <a href={`${API}/surrender/${token}/receipt.pdf`} target="_blank" rel="noopener noreferrer"
            data-testid="surrender-download-pdf"
            className="inline-flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 py-2 text-sm font-semibold text-navy hover:border-brand hover:text-brand transition-colors">
            <Download size={16} /> Download receipt (PDF)
          </a>
        </div>
        <p className="mt-4 text-center text-xs text-[#94A3B8]">Documented via TenantSeal · condition documentation only, no fault assigned.</p>
      </main>
    </div>
  );
}
